import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import { useNotificationStore } from './store/notificationStore';

interface OriginLike {
  protocol: string;
  hostname: string;
}

/**
 * Service workers may only be registered on a secure origin. Browsers permit
 * https:// and localhost; anything else (plain http, or a CDN that downgraded
 * the connection) must be skipped so registration cannot throw or silently
 * unregister an existing worker. (PWA-048-M-002)
 */
export function isSecureOrigin(location: OriginLike): boolean {
  return location.protocol === 'https:' || location.hostname === 'localhost';
}

/**
 * Register /sw.js, gated on service-worker support AND a secure origin.
 * Returns true when registration is attempted, false when intentionally
 * skipped, so callers and tests can rely on the signal.
 *
 * Closes the "deployed but never registered" gap: public/sw.js shipped but no
 * code ever installed it, leaving all SW-dependent behaviour (offline cache,
 * push) inert. (FRAME-044-M-001, IR-047-M-001, PWA-001-M-001, PWA-048-M-001,
 * FESTATE-145-M-001)
 */
export function registerServiceWorker(
  nav: Navigator = navigator,
  location: OriginLike = window.location,
): boolean {
  if (!('serviceWorker' in nav)) return false;
  if (!isSecureOrigin(location)) return false;

  nav.serviceWorker
    .register('/sw.js', { scope: '/' })
    .then((registration) => {
      // Notify users when a new worker is installed and waiting, rather than
      // silently swapping it in. (IR-047-M-002)
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          if (installing.state === 'installed' && nav.serviceWorker.controller) {
            useNotificationStore.getState().addToast({
              type: 'info',
              title: 'Update available',
              description: 'A new version is ready — refresh to update.',
            });
          }
        });
      });
    })
    .catch((err) => {
      console.error('Service worker registration failed', err);
    });

  return true;
}

/**
 * Production-only deterrent against self-XSS: an attacker socially engineers an
 * admin into pasting JS into DevTools, which would execute with their session
 * token (cros_admin_token in localStorage). The banner is the standard
 * mitigation. Returns true when the warning was emitted. (XSS-190-M-001)
 */
export function installSelfXssWarning(isProd: boolean = import.meta.env.PROD): boolean {
  if (!isProd) return false;
  console.warn('%cStop!', 'font-size:50px;color:red;font-weight:bold');
  console.warn(
    'Do not paste code here. Pasting code you do not understand can give an ' +
      'attacker control of your account — this is a known social-engineering attack.',
  );
  return true;
}

const rootElement = document.getElementById('root');
if (rootElement) {
  createRoot(rootElement).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  installSelfXssWarning();
  registerServiceWorker();
}
